import { CommunicationChannels } from '@bybeam/platform-types';
import { FormControl, FormLabel } from '@mui/material';
import Button from '@platform-ui-common/components/Button/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Modal from '@platform-ui-common/components/Modal/index.js';
import YesNoToggle from '@platform-ui-common/components/YesNoToggle/index.js';
import { fieldUpdate } from '@platform-ui-common/hooks/useForm/actions.js';
import useForm from '@platform-ui-common/hooks/useForm/hook.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import { useRef, useState } from 'react';
import useProfile from '../hooks/useProfile.js';
import { v4 as uuidV4 } from 'uuid';
import Message from '@platform-ui-common/components/Message/index.js';

const UserCommunicationsPreferencesModal = ({
  onClose = () => {},
}: {
  onClose?: () => void;
}) => {
  const [error, setError] = useState('');
  const { showSnackbar } = useSnackbar();
  const {
    user,
    partner,
    updateProfile: {
      update,
      results: { loading },
    },
  } = useProfile();
  const communicationPreferences = user?.core?.communicationPreferences;

  const { dispatch, formData, errors } = useForm({
    email: communicationPreferences?.email ?? true,
    sms: communicationPreferences?.sms ?? true,
  });

  const { sms, email } = formData;
  const descriptionId = useRef(`description-text-${uuidV4()}`);

  const CommunicationChannelToLabel: Record<
    CommunicationChannels,
    { label: string; description?: string }
  > = {
    [CommunicationChannels.EMAIL]: {
      label:
        'Would you like to get email notifications about your programs? (You will continue to receive important emails about your account)',
    },
    [CommunicationChannels.SMS]: {
      label: 'Would you like to get text messages about your programs?',
      description: `By selecting yes, you consent to receive text messages related to your application from ${partner.name}. Message frequency varies. Reply STOP to opt out or HELP for help. Message & data rates apply. You can manage your preferences
                    at any time.`,
    },
  };

  const doMutation = async (): Promise<void> => {
    if (!user?.core?.id) return;

    const {
      data: {
        user: { updateProfile },
      },
    } = await update({
      variables: {
        input: {
          name: user?.core.name,
          communicationPreferences: {
            email,
            sms,
          },
        },
      },
    });

    if (updateProfile?.metadata?.status === 200) {
      showSnackbar('Successfully updated');
      onClose();
    } else {
      setError('Something went wrong');
    }
  };

  return (
    <Modal title="Communication Preferences" isOpen onClickClose={onClose} size="M">
      <FormContainer
        onSubmit={doMutation}
        buttons={
          <div className="flex flex-col gap-6 items-center">
            <ErrorDisplay message={error} visible={!!error} />
            <Button type="submit" disabled={loading}>
              Save
            </Button>
          </div>
        }
      >
        <FormControl component="fieldset" variant="standard">
          {Object.values(CommunicationChannels).map((channel) => {
            return (
              <div key={channel} className="mb-6">
                <FormLabel component="legend">
                  {CommunicationChannelToLabel[channel].label}
                </FormLabel>
                <YesNoToggle
                  label={CommunicationChannelToLabel[channel].label}
                  onClick={(value): void => dispatch(fieldUpdate(channel, value))}
                  checked={formData[channel]}
                  error={errors[channel] as string}
                  border={false}
                  withAsterisk={!!CommunicationChannelToLabel[channel]?.description}
                  descriptionId={
                    CommunicationChannelToLabel[channel]?.description && descriptionId.current
                  }
                />
                {CommunicationChannelToLabel[channel]?.description ? (
                  <Message variant="info" id={descriptionId.current}>
                    {CommunicationChannelToLabel[channel]?.description}
                  </Message>
                ) : null}
              </div>
            );
          })}
        </FormControl>
      </FormContainer>
    </Modal>
  );
};

export default UserCommunicationsPreferencesModal;
